import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Folder, ListChecks, Clock, CheckCircle, XCircle, Users, Shield, UserX, AlertTriangle, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminDashboard() {
  const { session } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    registration_enabled: true,
    login_enabled: true
  });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [updatingSettings, setUpdatingSettings] = useState({});

  const fetchStats = useCallback(async () => {
    if (!session?.access_token) return;
    
    setLoading(true);
    try {
      // Fetch campaigns count
      const { count: campaignsCount } = await supabase
        .from('campaigns')
        .select('*', { count: 'exact', head: true });

      // Fetch open campaigns count
      const { count: openCampaignsCount } = await supabase
        .from('campaigns')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'open');

      // Fetch tasks count
      const { count: tasksCount } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true });

      // Fetch pending submissions count
      const { count: pendingCount } = await supabase
        .from('task_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      // Fetch approved submissions count
      const { count: approvedCount } = await supabase
        .from('task_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'approved');

      // Fetch rejected submissions count
      const { count: rejectedCount } = await supabase
        .from('task_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'rejected');

      // Fetch users count via backend API (bypasses RLS)
      let usersCount = 0;
      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/profiles/count`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          usersCount = data.count || 0;
        }
      } catch (err) {
        console.error('Error fetching users count:', err);
      }

      setStats({
        campaigns: campaignsCount || 0,
        openCampaigns: openCampaignsCount || 0,
        tasks: tasksCount || 0,
        pendingSubmissions: pendingCount || 0,
        approvedSubmissions: approvedCount || 0,
        rejectedSubmissions: rejectedCount || 0,
        users: usersCount,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings({
          registration_enabled: data.registration_enabled ?? true,
          login_enabled: data.login_enabled ?? true
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchSettings();
  }, [fetchStats, fetchSettings]);

  const updateSetting = async (key, value) => {
    setUpdatingSettings(prev => ({ ...prev, [key]: true }));
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Oturum bulunamadı');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/admin/settings/${key}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ value })
      });

      if (response.ok) {
        setSettings(prev => ({ ...prev, [key]: value }));
        
        if (key === 'registration_enabled') {
          toast.success(value ? 'Yeni üye alımı açıldı' : 'Yeni üye alımı durduruldu');
        } else if (key === 'login_enabled') {
          toast.success(value ? 'Giriş sistemi açıldı' : 'Tüm işlemler durduruldu - Kullanıcılar çıkış yapacak');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Ayar güncellenemedi');
      }
    } catch (error) {
      console.error('Error updating setting:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setUpdatingSettings(prev => ({ ...prev, [key]: false }));
    }
  };

  const [forcingLogout, setForcingLogout] = useState(false);

  const forceLogoutAll = async () => {
    setForcingLogout(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${BACKEND_URL}/api/admin/force-logout-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session?.access_token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'İşlem başarısız');
      toast.success(data.message || 'Tüm kullanıcıların oturumu kapatıldı');
    } catch (error) {
      toast.error(error.message || 'Bir hata oluştu');
    } finally {
      setForcingLogout(false);
    }
  };

  const statCards = [
    {
      title: 'Toplam Kampanya',
      value: stats?.campaigns,
      icon: Folder,
      color: 'bg-brand-ink',
      link: '/admin/campaigns',
    },
    {
      title: 'Açık Kampanya',
      value: stats?.openCampaigns,
      icon: Folder,
      color: 'bg-green-600',
      link: '/admin/campaigns',
    },
    {
      title: 'Toplam Görev',
      value: stats?.tasks,
      icon: ListChecks,
      color: 'bg-brand-ink',
      link: '/admin/tasks',
    },
    {
      title: 'Bekleyen Onay',
      value: stats?.pendingSubmissions,
      icon: Clock,
      color: 'bg-brand-red',
      link: '/admin/approvals',
    },
    {
      title: 'Onaylanan Görevler',
      value: stats?.approvedSubmissions,
      icon: CheckCircle,
      color: 'bg-emerald-500',
      link: '/admin/approvals',
    },
    {
      title: 'Reddedilen Görevler',
      value: stats?.rejectedSubmissions,
      icon: XCircle,
      color: 'bg-red-500',
      link: '/admin/approvals',
    },
    {
      title: 'Toplam Kullanıcı',
      value: stats?.users,
      icon: Users,
      color: 'bg-brand-ink',
      link: null,
    },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h2 className="font-brand text-2xl text-brand-ink mb-6">Dashboard</h2>

      {/* Security Controls */}
      <div className="mb-8">
        <h3 className="font-brand text-lg text-brand-ink mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-brand-ink" />
          Güvenlik Kontrolleri
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Registration Control */}
          <Card className={`bg-white shadow-sm ${!settings.registration_enabled ? 'border-2 border-brand-red' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${settings.registration_enabled ? 'bg-green-100' : 'bg-brand-red-soft'}`}>
                    <UserX className={`w-5 h-5 ${settings.registration_enabled ? 'text-green-600' : 'text-brand-red-deep'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Yeni Üye Alımı</p>
                    <p className="text-sm text-gray-500">
                      {settings.registration_enabled ? 'Yeni üyeler kayıt olabilir' : 'Yeni üye alımı durduruldu'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.registration_enabled}
                  onCheckedChange={(checked) => updateSetting('registration_enabled', checked)}
                  disabled={settingsLoading || updatingSettings.registration_enabled}
                  data-testid="registration-toggle"
                />
              </div>
              {!settings.registration_enabled && (
                <div className="mt-3 p-2 bg-brand-red-soft rounded-lg">
                  <p className="text-xs text-brand-red-deep flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Yeni üyeler "Üye alımı geçici bir süre durdurulmuştur." mesajı görecek
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Login Control - All Operations */}
          <Card className={`bg-white shadow-sm ${!settings.login_enabled ? 'border-2 border-red-400' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${settings.login_enabled ? 'bg-green-100' : 'bg-red-100'}`}>
                    <Shield className={`w-5 h-5 ${settings.login_enabled ? 'text-green-600' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Tüm İşlemler</p>
                    <p className="text-sm text-gray-500">
                      {settings.login_enabled ? 'Sistem normal çalışıyor' : 'Tüm işlemler durduruldu'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.login_enabled}
                  onCheckedChange={(checked) => updateSetting('login_enabled', checked)}
                  disabled={settingsLoading || updatingSettings.login_enabled}
                  data-testid="login-toggle"
                />
              </div>
              {!settings.login_enabled && (
                <div className="mt-3 p-2 bg-red-50 rounded-lg">
                  <p className="text-xs text-red-700 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Tüm kullanıcılar çıkış yapacak ve tekrar giriş yapamayacak
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sign everyone out (they can sign in again) */}
          <Card className="bg-white shadow-sm md:col-span-2">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gray-100">
                  <LogOut className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Herkesin Oturumunu Kapat</p>
                  <p className="text-sm text-gray-500">
                    Açık oturumlar en geç 1 dakika içinde kapanır, kullanıcılar tekrar giriş yapabilir. Adminler etkilenmez.
                  </p>
                </div>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" disabled={forcingLogout} data-testid="force-logout-all">
                    {forcingLogout ? 'Kapatılıyor...' : 'Oturumları Kapat'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Herkesin oturumu kapatılsın mı?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Şu an giriş yapmış tüm kullanıcılar çıkış yapacak ve tekrar giriş yapmaları gerekecek.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>İptal</AlertDialogCancel>
                    <AlertDialogAction onClick={forceLogoutAll} className="bg-red-600 hover:bg-red-700">
                      Oturumları Kapat
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, index) => (
          <Card key={index} className="bg-white shadow-sm hover:shadow-md transition-shadow">
            {card.link ? (
              <Link to={card.link} data-testid={`stat-card-${index}`}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    {card.title}
                  </CardTitle>
                  <div className={`p-2 rounded-lg ${card.color}`}>
                    <card.icon className="w-4 h-4 text-white" />
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    <p className="font-brand text-3xl text-brand-ink" data-testid={`stat-value-${index}`}>
                      {card.value}
                    </p>
                  )}
                </CardContent>
              </Link>
            ) : (
              <>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    {card.title}
                  </CardTitle>
                  <div className={`p-2 rounded-lg ${card.color}`}>
                    <card.icon className="w-4 h-4 text-white" />
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    <p className="font-brand text-3xl text-brand-ink" data-testid={`stat-value-${index}`}>
                      {card.value}
                    </p>
                  )}
                </CardContent>
              </>
            )}
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h3 className="font-brand text-lg text-brand-ink mb-4">Hızlı İşlemler</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/admin/campaigns"
            className="p-4 bg-white rounded-lg border border-gray-200 hover:border-brand-ink transition-colors flex items-center gap-3"
            data-testid="quick-action-campaigns"
          >
            <Folder className="w-5 h-5 text-brand-ink" />
            <span className="font-medium">Yeni Kampanya Oluştur</span>
          </Link>
          <Link
            to="/admin/tasks"
            className="p-4 bg-white rounded-lg border border-gray-200 hover:border-brand-ink transition-colors flex items-center gap-3"
            data-testid="quick-action-tasks"
          >
            <ListChecks className="w-5 h-5 text-brand-ink" />
            <span className="font-medium">Yeni Görev Ekle</span>
          </Link>
          <Link
            to="/admin/approvals"
            className="p-4 bg-white rounded-lg border border-gray-200 hover:border-brand-ink transition-colors flex items-center gap-3"
            data-testid="quick-action-approvals"
          >
            <CheckCircle className="w-5 h-5 text-brand-ink" />
            <span className="font-medium">Bekleyen Onayları Gör</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
